import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { AuthService } from '../_services/auth.service';
import { TokenStorageService } from '../_services/token-storage.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private expiryFlow$: Observable<string | null> | null = null;

  constructor(
    private injector: Injector,
    private tokenStorage: TokenStorageService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isAuthBypassUrl(req.url)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!this.isTokenExpiredError(error)) {
          // RxJS 6: throwError erwartet den Fehlerwert, keine Factory-Funktion
          return throwError(error);
        }

        if (!this.expiryFlow$) {
          this.expiryFlow$ = this.handleExpiredSession().pipe(
            finalize(() => {
              this.expiryFlow$ = null;
            }),
            shareReplay(1)
          );
        }

        return this.expiryFlow$.pipe(
          switchMap((token) => {
            if (!token) {
              return throwError(error);
            }
            return next.handle(this.cloneWithToken(req, token));
          })
        );
      })
    );
  }

  private handleExpiredSession(): Observable<string | null> {
    return from(
      Swal.fire({
        title: 'Sitzung abgelaufen',
        text: 'Ihre Anmeldung ist abgelaufen. Möchten Sie die Sitzung verlängern oder sich neu anmelden?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sitzung verlängern',
        cancelButtonText: 'Neu anmelden',
        reverseButtons: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
      })
    ).pipe(
      switchMap((result) => {
        if (result.isConfirmed) {
          const authService = this.injector.get(AuthService);
          return authService.refreshToken().pipe(
            switchMap((res) => {
              const token = res?.access_token;
              if (!token) {
                this.logoutToLogin('Sitzung konnte nicht verlängert werden.');
                return of(null as string | null);
              }
              this.tokenStorage.saveToken(token);
              Swal.fire({
                title: 'Sitzung verlängert',
                text: 'Sie können weiterarbeiten.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
              });
              return of(token);
            }),
            catchError(() => {
              this.logoutToLogin('Sitzung konnte nicht verlängert werden. Bitte erneut anmelden.');
              return of(null as string | null);
            })
          );
        }

        this.logoutToLogin();
        return of(null as string | null);
      })
    );
  }

  private logoutToLogin(message?: string): void {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('remember');
    this.tokenStorage.signOut();
    if (message) {
      Swal.fire({
        title: 'Abmeldung erforderlich',
        text: message,
        icon: 'info',
        confirmButtonText: 'Zum Login',
      }).then(() => {
        this.router.navigate(['/login'], { queryParams: { reason: 'session_expired' } });
      });
      return;
    }
    this.router.navigate(['/login'], { queryParams: { reason: 'session_expired' } });
  }

  private isTokenExpiredError(error: HttpErrorResponse): boolean {
    if (!error || error.status !== 401) {
      return false;
    }
    const statusText = String(error.error?.status || '').toLowerCase();
    // Nur explizit abgelaufenes JWT – nicht jede 401-Meldung mit „expired“
    return statusText === 'token is expired';
  }

  private isAuthBypassUrl(url: string): boolean {
    const lower = (url || '').toLowerCase();
    return (
      lower.includes('/api/auth/login') ||
      lower.includes('/api/auth/dispo/login') ||
      lower.includes('/api/auth/contractor/login') ||
      lower.includes('/api/auth/refresh') ||
      lower.includes('/api/settings/public')
    );
  }

  private cloneWithToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
